/**
 * EPS `GET /api/v1/vcn/wex/:merchantLogId/transactions` response.
 * Mirrors backend DTOs; `mergedTransactions` is the primary consumer shape.
 */

/** Backend `WexVcnMergedTransactionKind` (string enum) — wire `kind` on merged rows. */
export enum WexVcnMergedTransactionKinds {
  Transaction = 'TRANSACTION',
  Authorization = 'AUTHORIZATION',
}

export interface WexVcnMergedTransactionItemDto {
  kind: WexVcnMergedTransactionKinds
  occurredAt?: string
  id?: string
  type?: string
  amount?: number
  currency?: string
  merchantName?: string
  merchantCity?: string
  merchantState?: string
  merchantCountryCode?: string
  status?: string
  declineReasons?: string[]
}

/** Raw posted/cleared transaction row from WEX (mirrors backend `WexVcnTransactionItemDto`). */
export interface WexVcnTransactionItemDto {
  id: string
  transactionDate: string
  postingDate: string
  billingAmount: number
  billingCurrency: string
  sourceCurrency: string
  sourceAmount: number
  referenceNumber: string
  approvalCode: string
  /** Transaction type code from partner (e.g. P) */
  transactionType: string
  transactionCode: string
  mccCode: string
  merchantDescription: string
  merchantCity: string
  merchantState: string
  merchantPostalCode: string
  merchantCountryCode: string
  originalAmount: number
  isCredit: boolean
}

/** Raw authorization row from WEX (mirrors backend `WexVcnAuthorizationItemDto`). */
export interface WexVcnAuthorizationItemDto {
  /** Unique ID of authorization; may be blank for some processors */
  id?: string
  /** Date and time of authorization */
  authDateTime?: string
  /** Monetary amount of the authorization */
  authAmount?: number
  /** Type code of authorization message (varies by processor) */
  messageTypeCode?: string
  /** Type of message description */
  messageTypeDescription?: string
  responseCode?: string
  responseCodeDescription?: string
  approvalCode?: string
  /** "Approved" or "Declined" */
  statusCode?: string
  /** Zero or more reasons the authorization was declined */
  declineReasons?: string[]
  /** Available credit at the time of the authorization */
  availableCredit?: number
  /** Merchant Category Code (MCC) */
  merchantClassCode?: string
  merchantId?: string
  merchantName?: string
  merchantCity?: string
  /** Code for the state in which the merchant is located */
  merchantState?: string
  merchantPostalCode?: string
  /** Country code for where the merchant is located */
  merchantCountryCode?: string
  /** Three digit numeric country code */
  countryCode?: string
  /**
   * Conversion rate from the source currency to billing currency of the charged
   * card
   */
  currencyConversionRate?: number
  sourceCurrency?: string
  /** Monetary amount of the transaction in the source currency */
  sourceAmount?: number
}

export interface GetWexVcnTransactionsResponseDto {
  vcnProvider: string
  id: string
  last4: string
  transactions: WexVcnTransactionItemDto[]
  authorizations: WexVcnAuthorizationItemDto[]
  mergedTransactions: WexVcnMergedTransactionItemDto[]
}
